<template>
  <div
    class="re-tooltip"
    :class="{ 're-tooltip--standalone': !hasDefaultSlot }"
  >
    <slot />

    <span
      v-if="!hideTooltipIcon"
      class="tooltip-trigger"
      :class="triggerClasses"
      @mouseenter="show"
      @mouseleave="hide"
    >
      <re-icon
        v-if="resolvedIcon"
        :name="resolvedIcon"
        :color="resolvedIconColor"
        class="tooltip-trigger__icon"
      />

      <span
        v-else
        class="tooltip-trigger__text"
      >
        {{ triggerText }}
      </span>
    </span>

    <q-tooltip
      v-if="!hidden"
      v-model="visible"
      v-bind="{ ...placementAttrs, ...$attrs }"
      class="bg-dark text-body text-white text-body1"
      max-width="30em"
      :class="tooltipClasses"
    >
      <slot
        v-if="hasContentSlot"
        name="content"
      />

      <VueMarkdown
        v-else-if="isMarkdown"
        :source="markdownLabel"
      />

      <template v-else-if="Array.isArray(label)">
        <div
          v-for="text in label"
          :key="text"
        >
          {{ text }}
        </div>
      </template>

      <div v-else>
        {{ label }}
      </div>
    </q-tooltip>
  </div>
</template>

<script setup lang="ts">
import { computed, onScopeDispose, ref, useSlots, watch } from 'vue'
import VueMarkdown from 'vue-markdown-render'

type Direction = 'top' | 'right' | 'bottom' | 'left'
type TooltipClass = string | Record<string, boolean> | TooltipClass[]

interface Props {
  label?: string | string[]
  triggerText?: string | number
  triggerColor?: string
  hideTooltipIcon?: boolean
  isVisible?: boolean
  isWarning?: boolean
  isError?: boolean
  tooltipClasses?: TooltipClass
  direction?: Direction
  noPadding?: boolean
  icon?: string
  iconColor?: string
  isMarkdown?: boolean
  isExp?: boolean
  isSmaller?: boolean
  size?: string
  hidden?: boolean
  isInitiallyVisibleFor?: number
}

const {
  label = '',
  triggerText = '?',
  triggerColor = 'bg-info-light text-info',
  hideTooltipIcon = false,
  isVisible = false,
  isWarning = false,
  isError = false,
  tooltipClasses = [],
  direction = 'top',
  icon = undefined,
  iconColor = undefined,
  isMarkdown = false,
  isExp = false,
  isSmaller = false,
  size = undefined,
  hidden = false,
  isInitiallyVisibleFor = 0,
} = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const slots = useSlots()
const hasDefaultSlot = computed(() => !!slots.default)
const hasContentSlot = computed(() => !!slots.content)
const visible = ref(isVisible)
let initialVisibilityTimer: ReturnType<typeof setTimeout> | undefined

const resolvedIcon = computed(() => {
  if (isWarning)
    return 'mdi-alert'
  if (isError)
    return 'mdi-exclamation-thick'
  return icon
})

const resolvedIconColor = computed(() =>
  iconColor ?? (isWarning || isError ? 'white' : 'primary'),
)

const resolvedTriggerColor = computed(() => {
  if (isWarning)
    return 'bg-warning text-white'
  if (isError)
    return 'bg-negative text-white'
  return triggerColor
})

const triggerClasses = computed(() => [
  resolvedTriggerColor.value,
  size && `re-${size}`,
  {
    exponent: isExp,
    smaller: isSmaller || isExp,
  },
])

const markdownLabel = computed(() =>
  Array.isArray(label) ? label.join('\n') : label,
)

const placementAttrs = computed(() => {
  const placements = {
    top: {
      anchor: 'top middle',
      self: 'bottom middle',
      transitionShow: 'jump-up',
      transitionHide: 'jump-down',
    },
    right: {
      anchor: 'center right',
      self: 'center left',
      transitionShow: 'jump-right',
      transitionHide: 'jump-left',
    },
    bottom: {
      anchor: 'bottom middle',
      self: 'top middle',
      transitionShow: 'jump-down',
      transitionHide: 'jump-up',
    },
    left: {
      anchor: 'center left',
      self: 'center right',
      transitionShow: 'jump-left',
      transitionHide: 'jump-right',
    },
  } as const
  return placements[direction]
})

function show() {
  visible.value = true
}

function hide() {
  visible.value = false
}

function scheduleInitialVisibility() {
  if (initialVisibilityTimer)
    clearTimeout(initialVisibilityTimer)
  if (isInitiallyVisibleFor <= 0 || isVisible)
    return

  show()
  initialVisibilityTimer = setTimeout(hide, isInitiallyVisibleFor)
}

watch(() => isVisible, value => visible.value = value)
watch(() => isInitiallyVisibleFor, scheduleInitialVisibility, { immediate: true })
onScopeDispose(() => initialVisibilityTimer && clearTimeout(initialVisibilityTimer))
</script>

<style lang="scss" scoped>
:deep(h3) {
  color: $base;
  font-size: 1.3rem;
}

.re-tooltip {
  display: flex;
  align-items: center;

  &--standalone {
    display: inline-flex;
  }
}

.tooltip-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 1.15em;
  height: 1.15em;
  margin-left: 0.5em;
  border-radius: 50%;
  cursor: help;
  font-weight: 900;
  line-height: 1;

  &__icon {
    display: block;
    font-size: 0.8em;
    line-height: 1;
  }

  &__text {
    display: block;
    font-size: 0.8em;
    line-height: 1;
  }

  &.exponent {
    margin-left: 0.15em;
    transform: translateY(-0.25em);
  }

  &.smaller {
    width: 0.75em;
    height: 0.75em;

    .tooltip-trigger__icon,
    .tooltip-trigger__text {
      font-size: 0.5em;
    }
  }
}
</style>
